import { useState, useEffect } from 'react'
import { CheckCircle2, Minus, Plus } from 'lucide-react'
import { useData } from 'vike-react/useData'
import { Head } from 'vike-react/Head'
import type { Data } from './+data'
import { createQuoteFromCart } from '../../src/api/quotes'
import { Button } from '../../src/components/ui/button'
import { cleanBrDescription, truncate } from '../../src/lib/utils'
import { useAuth } from '../../src/context/AuthContext'

interface FormState {
  customerName: string
  customerEmail: string
  customerCompany: string
  customerPhone: string
  customerReference: string
  notes: string
}

const empty: FormState = {
  customerName: '',
  customerEmail: '',
  customerCompany: '',
  customerPhone: '',
  customerReference: '',
  notes: '',
}

export default function ProductQuotePage() {
  const { product, lang } = useData<Data>()
  const { user } = useAuth()
  const [form, setForm] = useState<FormState>(empty)
  const [quantity, setQuantity] = useState<number>(product.moq && product.moq > 0 ? product.moq : 1)

  useEffect(() => {
    if (!user) return
    setForm(f => ({
      ...f,
      customerName: f.customerName || `${user.first_name} ${user.last_name}`.trim(),
      customerEmail: f.customerEmail || user.email,
      customerCompany: f.customerCompany || (user.company ?? ''),
    }))
  }, [user])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [quoteNumber, setQuoteNumber] = useState<string | null>(null)

  const minQty = product.moq && product.moq > 0 ? product.moq : 1
  // Avoid showing the MPN twice when the catalog has no proper display title:
  // fall back to a cleaned short_description, otherwise just show the MPN once below.
  const displayName = product.display_title && product.display_title !== product.normalized_mpn
    ? product.display_title
    : (product.short_description ? truncate(cleanBrDescription(product.short_description), 90) : null)

  function field(name: keyof FormState) {
    return {
      value: form[name],
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setForm(f => ({ ...f, [name]: e.target.value })),
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)

    try {
      const res = await createQuoteFromCart({
        customerName: form.customerName.trim(),
        customerEmail: form.customerEmail.trim(),
        customerCompany: form.customerCompany.trim(),
        customerPhone: form.customerPhone.trim() || undefined,
        customerReference: form.customerReference.trim() || undefined,
        notes: form.notes.trim() || undefined,
        items: [
          {
            mpn: product.normalized_mpn,
            title: product.display_title,
            unitPrice: product.effective_price ?? '0',
            quantity: Math.max(minQty, quantity),
          },
        ],
      })
      setQuoteNumber(res.quote_number)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Er is iets misgegaan. Probeer het later opnieuw.')
    } finally {
      setSubmitting(false)
    }
  }

  const pageTitle = `Offerte aanvragen — ${product.display_title} — Global Control Group`

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="robots" content="noindex" />
      </Head>

      <div className="container mx-auto max-w-2xl px-4 py-10">
        {quoteNumber ? (
          <div className="flex flex-col items-center gap-4 py-12 text-center">
            <CheckCircle2 className="h-14 w-14 text-green-500" />
            <h1 className="text-2xl font-bold text-neutral-900">Offerteverzoek ontvangen</h1>
            <p className="text-neutral-600">
              Uw aanvraag is geregistreerd onder referentie{' '}
              <span className="font-mono font-semibold">{quoteNumber}</span>.
            </p>
            <p className="text-sm text-neutral-500">
              U ontvangt binnen 1–2 werkdagen een offerte op het opgegeven e-mailadres.
            </p>
            <a href={`/${lang}/products`} className="mt-4 text-sm text-primary hover:underline">
              Terug naar producten
            </a>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-neutral-900 mb-1">Offerte aanvragen</h1>
            <p className="text-sm text-neutral-500 mb-6">
              Vul uw gegevens in. Wij nemen binnen 1–2 werkdagen contact met u op.
            </p>

            <div className="bg-white border border-neutral-200 rounded-lg px-5 py-4 mb-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400 mb-1">Product</p>
              {displayName && <p className="font-semibold text-neutral-900">{displayName}</p>}
              <p className={`text-sm text-neutral-500 font-mono${displayName ? '' : ' font-semibold text-neutral-900 text-base'}`}>{product.normalized_mpn}</p>
              {product.effective_price && (
                <p className="text-sm text-neutral-600 mt-1">
                  Indicatieve prijs: <span className="font-semibold">€{product.effective_price}</span> ex. btw
                </p>
              )}

              <div className="flex items-center gap-3 mt-4">
                <label htmlFor="q-quantity" className="text-sm font-medium text-neutral-700">
                  Aantal
                </label>
                <div className="flex items-center border border-neutral-300 rounded-md overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setQuantity(q => Math.max(minQty, q - 1))}
                    className="px-2 py-1.5 hover:bg-neutral-50 disabled:opacity-40"
                    disabled={quantity <= minQty}
                    aria-label="Aantal verlagen"
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <input
                    id="q-quantity"
                    type="number"
                    min={minQty}
                    value={quantity}
                    onChange={e => {
                      const n = parseInt(e.target.value, 10)
                      if (Number.isFinite(n)) setQuantity(Math.max(minQty, n))
                    }}
                    className="w-16 px-2 py-1.5 text-center text-sm bg-white focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <button
                    type="button"
                    onClick={() => setQuantity(q => q + 1)}
                    className="px-2 py-1.5 hover:bg-neutral-50"
                    aria-label="Aantal verhogen"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
                {minQty > 1 && (
                  <span className="text-xs text-neutral-500">Min. {minQty} stuks</span>
                )}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-5 bg-white border border-neutral-200 rounded-lg px-5 py-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="flex flex-col gap-1">
                  <label htmlFor="q-name" className="text-sm font-medium text-neutral-700">
                    Naam <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="q-name"
                    type="text"
                    required
                    autoComplete="name"
                    placeholder="Jan de Vries"
                    className="border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    {...field('customerName')}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label htmlFor="q-email" className="text-sm font-medium text-neutral-700">
                    E-mailadres <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="q-email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="jan@bedrijf.nl"
                    className="border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    {...field('customerEmail')}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="flex flex-col gap-1">
                  <label htmlFor="q-company" className="text-sm font-medium text-neutral-700">
                    Bedrijfsnaam <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="q-company"
                    type="text"
                    required
                    autoComplete="organization"
                    placeholder="Machinefabriek B.V."
                    className="border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    {...field('customerCompany')}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label htmlFor="q-phone" className="text-sm font-medium text-neutral-700">
                    Telefoonnummer
                  </label>
                  <input
                    id="q-phone"
                    type="tel"
                    autoComplete="tel"
                    placeholder="+31 85 060 2910"
                    className="border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    {...field('customerPhone')}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <label htmlFor="q-reference" className="text-sm font-medium text-neutral-700">
                  Uw referentienummer / PO-nummer
                </label>
                <input
                  id="q-reference"
                  type="text"
                  placeholder="PO-2026-0042"
                  className="border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  {...field('customerReference')}
                />
              </div>

              <div className="flex flex-col gap-1">
                <label htmlFor="q-notes" className="text-sm font-medium text-neutral-700">
                  Opmerkingen
                </label>
                <textarea
                  id="q-notes"
                  rows={3}
                  placeholder="Levertijd, volume, technische vragen…"
                  className="border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  {...field('notes')}
                />
              </div>

              {error && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                  {error}
                </p>
              )}

              <Button type="submit" variant="primary" size="lg" disabled={submitting} className="w-full sm:w-auto self-end">
                {submitting ? 'Versturen…' : 'Offerte aanvragen'}
              </Button>
            </form>
          </>
        )}
      </div>
    </>
  )
}
